import { z } from 'zod';

// ADHD-specific validation schemas with user-friendly error messages

// Todo validation schema
export const todoSchema = z.object({
  title: z.string()
    .min(1, "Todo needs a title - even a short one!")
    .max(200, "Keep it under 200 characters - shorter is better for focus!")
    .trim(),
  
  description: z.string()
    .max(1000, "Description is getting long - maybe break it into subtasks?")
    .optional()
    .transform(val => val?.trim()),
  
  priority: z.enum(['low', 'medium', 'high', 'urgent'], {
    errorMap: () => ({ message: "Pick a priority: low, medium, high, or urgent" })
  }).default('medium'),
  
  status: z.enum(['pending', 'in_progress', 'completed', 'blocked'], {
    errorMap: () => ({ message: "Status must be: pending, in_progress, completed, or blocked" })
  }).default('pending'),
  
  due_date: z.string()
    .datetime({ message: "Due date must be a valid date and time" })
    .optional()
    .refine(date => {
      if (!date) return true;
      return new Date(date) > new Date();
    }, { message: "Due date can't be in the past - let's be realistic!" }),
  
  project_id: z.string()
    .uuid({ message: "Project ID must be a valid UUID" })
    .optional(),
  
  tags: z.array(z.string().trim().min(1).max(50))
    .max(10, "Max 10 tags - too many gets overwhelming!")
    .default([]),
  
  estimated_minutes: z.number()
    .int({ message: "Time estimate must be a whole number" })
    .min(1, "At least 1 minute - even small tasks take some time!")
    .max(480, "Over 8 hours? Maybe break this into smaller tasks!")
    .optional(),
  
  notes: z.string()
    .max(2000, "Notes are getting very long - consider breaking into multiple todos")
    .default('')
    .transform(val => val.trim())
});

// Project validation schema
export const projectSchema = z.object({
  name: z.string()
    .min(1, "Project needs a name!")
    .max(100, "Keep project name under 100 characters")
    .trim(),
  
  description: z.string()
    .max(500, "Project description is getting long - keep it focused!")
    .optional()
    .transform(val => val?.trim()),
  
  color: z.string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Color must be a valid hex code (like #ff0000)")
    .default('#3b82f6'),
  
  deadline: z.string()
    .datetime({ message: "Deadline must be a valid date and time" })
    .optional()
    .refine(date => {
      if (!date) return true;
      return new Date(date) > new Date();
    }, { message: "Deadline can't be in the past!" }),
  
  todos: z.array(z.string().uuid())
    .default([])
});

// User input validation for forms
export const userInputSchema = z.object({
  email: z.string()
    .email("That doesn't look like a valid email address")
    .max(320, "Email address is too long"),
  
  name: z.string()
    .min(1, "Name is required")
    .max(100, "Name is too long")
    .trim()
    .refine(val => !/^\s*$/.test(val), { message: "Name can't be just spaces" }),
  
  feedback: z.string()
    .min(10, "Please give us at least 10 characters of feedback")
    .max(2000, "Feedback is getting very long - maybe send an email instead?")
    .trim()
});

// AI function call validation
export const aiFunctionSchema = z.object({
  function_name: z.string()
    .min(1, "Function name is required")
    .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, "Function name must be valid identifier"),
  
  arguments: z.record(z.any()).default({}),
  
  context: z.object({
    user_id: z.string().uuid().optional(),
    session_id: z.string().optional(),
    timestamp: z.string().datetime().optional()
  }).optional()
});

// Session validation
export const sessionSchema = z.object({
  task_id: z.string()
    .uuid({ message: "Task ID must be valid" })
    .optional(),
  
  duration_minutes: z.number()
    .int()
    .min(1, "Session must be at least 1 minute")
    .max(240, "Sessions over 4 hours aren't recommended for ADHD focus!")
    .default(25),
  
  check_in_frequency: z.number()
    .int()
    .min(1, "Check-ins must be at least 1 minute apart")
    .max(60, "Check-ins every hour+ defeats the purpose!")
    .default(10),
  
  session_type: z.enum(['body_doubling', 'pomodoro', 'deep_work', 'quick_task'])
    .default('body_doubling')
});

// API response validation
export const apiResponseSchema = z.object({
  success: z.boolean(),
  data: z.any().optional(),
  error: z.string().optional(),
  code: z.string().optional(),
  timestamp: z.string().datetime().optional()
});

// Validation utilities
export class ValidationError extends Error {
  public readonly field?: string;
  public readonly userMessage: string;
  
  constructor(message: string, field?: string, userMessage?: string) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.userMessage = userMessage || message;
  }
}

/**
 * Validate data against a schema with ADHD-friendly error messages
 */
export function validateData<T>(schema: z.ZodSchema<T>, data: unknown): T {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const firstError = error.errors[0];
      throw new ValidationError(
        firstError.message,
        firstError.path.join('.'),
        firstError.message
      );
    }
    throw error;
  }
}

/**
 * Safe validation that returns result with errors instead of throwing
 */
export function safeValidate<T>(schema: z.ZodSchema<T>, data: unknown): {
  success: boolean;
  data?: T;
  errors?: Array<{ field: string; message: string }>;
} {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  const errors = result.error.errors.map(err => ({
    field: err.path.join('.'),
    message: err.message
  }));
  
  return { success: false, errors };
}

/**
 * Sanitize HTML input to prevent XSS
 */
export function sanitizeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\//g, '&#x2F;');
}

/**
 * Validate and sanitize user text input
 */
export function validateUserText(input: string, maxLength: number = 1000): string {
  if (typeof input !== 'string') {
    throw new ValidationError('Input must be text');
  }
  
  const trimmed = input.trim();
  
  if (trimmed.length === 0) {
    throw new ValidationError('Input cannot be empty');
  }
  
  if (trimmed.length > maxLength) {
    throw new ValidationError(`Input too long (max ${maxLength} characters)`);
  }
  
  // Check for potential malicious patterns
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /data:text\/html/i
  ];
  
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(trimmed)) {
      throw new ValidationError('Input contains potentially harmful content');
    }
  }
  
  return sanitizeHtml(trimmed);
}

/**
 * Validate file uploads
 */
export function validateFile(file: File, options: {
  maxSize?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
} = {}): void {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'text/plain'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.txt']
  } = options;
  
  if (file.size > maxSize) {
    throw new ValidationError(
      `File too large (max ${Math.round(maxSize / 1024 / 1024)}MB)`
    );
  }
  
  if (!allowedTypes.includes(file.type)) {
    throw new ValidationError(
      `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`
    );
  }
  
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!allowedExtensions.includes(extension)) {
    throw new ValidationError(
      `File extension not allowed. Allowed extensions: ${allowedExtensions.join(', ')}`
    );
  }
}

// Export commonly used schemas
export const schemas = {
  todo: todoSchema,
  project: projectSchema,
  userInput: userInputSchema,
  aiFunction: aiFunctionSchema,
  session: sessionSchema,
  apiResponse: apiResponseSchema
} as const;